import {
  selectGroupForm,
  selectGroupsPagination,
  selectTicketCache,
  selectTicketFormDetail,
  selectTicketGroups,
} from "./selector";

import { actions as appActions } from "app/redux/slice";
import { ticketGroupsActions as actions } from "./slice";

import { call, delay, put, select, takeLatest } from "redux-saga/effects";

import { TICKET_API_URL } from "utils/constants";
import { handleApiError } from "utils/errorHandler";
import { request } from "utils/request";
import toastService from "utils/toast";

import { fetchNoMatchGroupBy } from "../../../helpers";
import {
  getNextTicketBatch,
  initializeAllGroupsPagination,
  updatePaginationState,
} from "../../../helpers/paginationHelper";

import { BoardViewType } from "app/types";
import { selectDraftBoardForm } from "../../../components/Settings/redux/selector";
import { IBoardState } from "../../../components/Settings/types";
import { IGroupJsonPayload, PaginatedBoardGroup, TicketGroup } from "../types";

const activeRequests = new Map<string, boolean>();

/* =====================================================
   FETCH TICKETS BY IDS
===================================================== */

export function* fetchTicketsByIds(ticketIds: string[]) {
  if (!ticketIds.length) {
    return [];
  }

  const { data, error } = yield call(request, `${TICKET_API_URL}ticket/list`, {
    method: "POST",
    body: JSON.stringify({ ticketIds }),
  });

  if (error || !data) {
    handleApiError(error);
    return null;
  }

  return data?.data || [];
}

/* =====================================================
   CREATE GROUP
===================================================== */

export function* createTicketGroup() {
  try {
    const groupForm = yield select(selectGroupForm);
    console.log("groupForm", groupForm);
  } catch (error) {
    console.log("error", error);
  }
}


/* =====================================================
   CREATE TICKET
===================================================== */

export function* createTicketRequest(action: {
  payload: {
    projectId: string;
    boardId: string;
    groupId: string;
    callback?: () => void;
  };
}) {
  yield delay(500);

  try {
    yield put(actions.setIsLoading(true));

    const requestURL = `${TICKET_API_URL}ticket/create`;

    const ticketForm = yield select(selectTicketFormDetail);

    const body = {
      ...ticketForm,
      projectId: action.payload.projectId,
      boardId: action.payload.boardId,
      groupId: action.payload.groupId,
    };

    const { data, err } = yield call(request, requestURL, {
      method: "POST",
      body: JSON.stringify(body),
    });

    yield put(actions.setIsLoading(false));

    if (err || !data) {
      handleApiError(err);
      return;
    }

    toastService.success(data?.message);

    const createdTicket = data?.data;

    yield put(actions.clearCreateTicketForm());

    yield put(
      appActions.setTicketLocally({
        ticket: createdTicket,
        groupId: action.payload.groupId,
      }),
    );

    yield put(actions.addTicketToGroup(createdTicket));

    action.payload.callback?.();
  } catch (err: any) {
    yield put(actions.setIsLoading(false));
    handleApiError(err);
  }
}

/* =====================================================
   FETCH GROUP JSON (Kanban/List aware)
===================================================== */

export function* fetchBoardsGroupsJsonRequest(action: {
  payload: IGroupJsonPayload;
}) {
  try {
    const { boardId, groupBy, subgroupBy, callback } = action.payload;
    const { viewType }: IBoardState = yield select(selectDraftBoardForm);
    yield put(actions.setGroupingLoader(true));

    // List board: ignore subgroupBy
    const finalSubgroupBy =
      viewType === BoardViewType.LIST ? "none" : subgroupBy;

    const requestURL = `${TICKET_API_URL}board/group-json/${boardId}/${groupBy}/${finalSubgroupBy}`;

    const { data, err } = yield call(request, requestURL, { method: "GET" });

    yield put(actions.setGroupingLoader(false));

    if (err || !data) {
      handleApiError(err);
      return;
    }

    let updatedGroups = data?.data?.map((group: TicketGroup) =>
      fetchNoMatchGroupBy(groupBy, group),
    );

    // List board: remove subGroups
    if (viewType === BoardViewType.LIST) {
      updatedGroups = updatedGroups.map((g) => ({
        ...g,
        subGroups: [],
      }));
    }

    yield put(actions.setBoardGroupsJson(updatedGroups));

    // Fetch tickets for the board
    yield call(fetchGroupTicketsRequest, updatedGroups);

    callback?.();
  } catch (err) {
    yield put(actions.setGroupingLoader(false));
    handleApiError(err);
  }
}

/* =====================================================
   FETCH INITIAL GROUP TICKETS
===================================================== */

export function* fetchGroupTicketsRequest(directGroups?: TicketGroup[]) {
  try {
    yield put(actions.setGroupingTicketsLoader(true));

    const ticketCache = yield select(selectTicketCache);

    const groups: TicketGroup[] =
      directGroups && directGroups.length
        ? directGroups
        : yield select(selectTicketGroups);

    /*
     STEP 1: Collect groups + subgroups
    */
    const allGroups = groups.flatMap((group) => {
      const subGroups = (group.subGroups || []).map((sub) => ({
        _id: sub._id,
        key: `${group._id}_${sub._id}`,
        tickets: sub.tickets || (sub as any).ticketIds || [],
      }));

      return [{ _id: group._id, tickets: group.tickets || [] }, ...subGroups];
    });

    const paginationMap = initializeAllGroupsPagination(allGroups, 10);

    /*
     STEP 2: Find missing ticket ids
    */
    const initialTicketIds = Object.values(paginationMap).flatMap(
      (g: PaginatedBoardGroup) => g.loadedTicketIds || [],
    );

    const cachedIds = new Set(Object.keys(ticketCache));

    const missingIds = initialTicketIds.filter((id) => !cachedIds.has(id));

    let ticketsDataMap = { ...ticketCache };

    /*
     STEP 3: Fetch missing tickets
    */
    if (missingIds.length) {
      const tickets: any[] | null = yield call(fetchTicketsByIds, missingIds);

      if (tickets === null) {
        return;
      }

      const newTicketsMap: Record<string, any> = {};

      tickets.forEach((ticket: any) => {
        newTicketsMap[ticket._id] = ticket;
      });

      ticketsDataMap = {
        ...ticketsDataMap,
        ...newTicketsMap,
      };

      yield put(actions.updateTicketCache(newTicketsMap));
    }

    /*
     STEP 4: Hydrate groups
    */
    const finalGroups = groups.map((group) => {
      const groupPagination = paginationMap[group._id];

      const groupTicketsList = (groupPagination?.loadedTicketIds || [])
        .map((id) => ticketsDataMap[id])
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);

      const hydratedSubGroups = (group.subGroups || []).map((subGroup) => {
        const subPagination = paginationMap[`${group._id}_${subGroup._id}`];

        const subTicketsList = (subPagination?.loadedTicketIds || [])
          .map((id) => ticketsDataMap[id])
          .filter(Boolean)
          .sort((a, b) => a.order - b.order);

        return {
          ...subGroup,
          ticketsList: subTicketsList,
        };
      });

      return {
        ...group,
        ticketsList: groupTicketsList,
        subGroups: hydratedSubGroups,
      };
    });

    yield put(actions.setGroupsPagination(paginationMap));
    yield put(actions.setBoardGroupsJson(finalGroups));
  } catch (err) {
    handleApiError(err);
  } finally {
    yield put(actions.setGroupingTicketsLoader(false));
  }
}

/* =====================================================
   LOAD MORE TICKETS (GROUP + SUBGROUP SAFE)
===================================================== */

export function* loadMoreGroupTicketsRequest(action: {
  payload: { groupId: string; batchSize?: number; callback?: () => void };
}) {
  const { groupId, batchSize = 10, callback } = action.payload;

  const requestKey = `loadMore_${groupId}`;

  try {
    if (activeRequests.get(requestKey)) {
      callback?.();
      return;
    }

    activeRequests.set(requestKey, true);

    const paginationMap = yield select(selectGroupsPagination);
    const pagination: PaginatedBoardGroup = paginationMap[groupId];

    if (!pagination?.hasMore) {
      activeRequests.delete(requestKey);
      callback?.();
      return;
    }

    const nextTicketIds = getNextTicketBatch(pagination, batchSize);

    if (!nextTicketIds.length) {
      activeRequests.delete(requestKey);
      callback?.();
      return;
    }

    const newTickets: any[] | null = yield call(fetchTicketsByIds, nextTicketIds);

    if (newTickets === null) {
      return;
    }

    const newTicketsMap: Record<string, any> = {};
    newTickets.forEach((t: any) => {
      newTicketsMap[t._id] = t;
    });

    yield put(actions.updateTicketCache(newTicketsMap));

    const updatedPagination = updatePaginationState(pagination, nextTicketIds);

    yield put(
      actions.setGroupsPagination({
        ...paginationMap,
        [groupId]: updatedPagination,
      }),
    );

    const fullCache = yield select(selectTicketCache);
    const groups = yield select(selectTicketGroups);

    const updatedGroups = groups.map((group) => {
      if (group._id === groupId) {
        const list = updatedPagination.loadedTicketIds
          .map((id) => fullCache[id])
          .filter(Boolean)
          .sort((a, b) => a.order - b.order);

        return { ...group, ticketsList: list };
      }

      if (group.subGroups?.length) {
        const updatedSubs = group.subGroups.map((sub) => {
          const combinedKey = `${group._id}_${sub._id}`;
          if (sub._id === groupId || combinedKey === groupId) {
            const list = updatedPagination.loadedTicketIds
              .map((id) => fullCache[id])
              .filter(Boolean)
              .sort((a, b) => a.order - b.order);

            return { ...sub, ticketsList: list };
          }

          return sub;
        });

        return { ...group, subGroups: updatedSubs };
      }

      return group;
    });

    yield put(actions.setBoardGroupsJson(updatedGroups));

    callback?.();
  } catch (err) {
    handleApiError(err);
  } finally {
    activeRequests.delete(requestKey);
  }
}

/* =====================================================
   ROOT SAGA
===================================================== */

export default function* ticketGroupsSaga() {
  yield takeLatest(actions.createTicketGroup, createTicketGroup);
  yield takeLatest(actions.setCreateTicket, createTicketRequest);
  yield takeLatest(actions.fetchBoardsGroupsJson, fetchBoardsGroupsJsonRequest);
  yield takeLatest(actions.loadMoreGroupTickets, loadMoreGroupTicketsRequest);
}
